import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {VocabularyService} from '../../services/vocabulary.service';
import {VocabularyPipe} from '../../shared/vocabulary.pipe';
import {Component, OnInit} from '@angular/core';
import {BeraterUebersichtService} from '../../services/beraterUebersicht.service';
import {BeraterUebersicht} from '../../shared/model/beraterUebersicht.model';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import {MatDrawer, MatDrawerContainer} from '@angular/material/sidenav';
import {MatButton, MatIconButton, MatMiniFabButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {SkillsComponent} from '../consultant/skills/skills.component';
import {DatePipe, NgClass} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatFormField, MatInput, MatLabel} from '@angular/material/input';
import {MatMenu, MatMenuTrigger} from '@angular/material/menu';
import {MatCheckbox} from '@angular/material/checkbox';
import {createFilterOptions, FilterOptions} from '../../shared/model/filter.model';
import {IFaehigkeit} from '../../shared/model/faehigkeit.model';
import {FaehigkeitenService} from '../../services/faehigkeiten.service';
import {MessageService} from '../../shared/components/message.service';
import {SorterService} from '../../services/sorter.service';
import {QuestionAnswerComponent} from '../../shared/components/question-answer/question-answer.component';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {RouterLink} from '@angular/router';
import {MatSort, MatSortHeader, Sort} from '@angular/material/sort';
import moment from 'moment';
import {DropdownCardFieldComponent} from '../../shared/components/dropdown-card-field/dropdown-card-field.component';
import {BeraterService} from '../../services/berater.service';
import {AdvisorService} from '../../services/advisor.service';
import {Advisor} from '../../shared/model/advisor.model';
import {loadAllPages} from '../../shared/load-all-pages';

@Component({
  selector: 'app-bench',
  imports: [
    VocabularyPipe,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatRow,
    MatRowDef,
    MatHeaderRowDef,
    MatDrawerContainer,
    MatDrawer,
    MatIconButton,
    MatIcon,
    SkillsComponent,
    DatePipe,
    TranslatePipe,
    FormsModule,
    MatFormField,
    MatInput,
    MatMiniFabButton,
    NgClass,
    MatMenu,
    MatMenuTrigger,
    MatCheckbox,
    MatButton,
    QuestionAnswerComponent,
    MatProgressSpinner,
    RouterLink,
    MatSort,
    MatSortHeader,
    RouterLink,
    DropdownCardFieldComponent,
    MatLabel,
    ReactiveFormsModule
  ],
  templateUrl: './bench.component.html',
  styleUrl: './bench.component.scss',
  standalone: true
})
export class BenchComponent implements OnInit {
  benchedConsultants: BeraterUebersicht[];
  displayedColumns: string[] = ['name', 'available', 'role', 'note', 'placeOfResidence', 'advisor', 'applicant'];
  selectedConsultant: BeraterUebersicht;
  isDrawerOpen: boolean = false;
  isConsultantsLoading: boolean = true;
  amount: number = 0;
  filteredConsultants: BeraterUebersicht[];
  filter: FilterOptions = createFilterOptions();
  allSkills: IFaehigkeit[] = [];
  form: FormGroup;
  originalNote: string = '';
  advisorOptions: Advisor[] = [];
  advisorMap = new Map<string, Advisor>();

  constructor(
    private consultantService: BeraterUebersichtService,
    private skillService: FaehigkeitenService,
    private messageService: MessageService,
    protected sorterService: SorterService,
    private fb: FormBuilder,
    private beraterService: BeraterService,
    private advisorService: AdvisorService,
    private vocabulary: VocabularyService,
  ) {
    this.vocabulary.changes$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.skillLabel = s => this.vocabulary.skillName(s);
      this.sortSkills();
    });
  }

  skillLabel = (s: IFaehigkeit) => this.vocabulary.skillName(s);

  private sortSkills() {
    this.allSkills = this.vocabulary.sort(this.allSkills, s => this.vocabulary.skillName(s));
  }

  compareSkills(a: IFaehigkeit, b: IFaehigkeit): boolean {
    return a?.id == b?.id;
  }

  ngOnInit() {
    this.getSupervisors();
    this.getConsultants();
    this.getAllSkills();

    this.form = this.fb.group({
      note: ''
    });

    if (this.selectedConsultant?.note) {
      this.form.patchValue({note: this.selectedConsultant.note.note});
    }
  }

  getSupervisors() {
    loadAllPages<Advisor>((page, size) => this.advisorService.getAdvisors(page, size), 200).subscribe({
      next: (advisors) => {
        this.advisorOptions = advisors;
        this.advisorMap = new Map(advisors.map(a => [a.id, a]));
        this.applyAdvisors();
      }
    });
  }

  // Betreuer und Berater laden parallel; wer zuletzt ankommt, setzt die Zuordnung.
  private applyAdvisors() {
    this.benchedConsultants = this.withAdvisor(this.benchedConsultants);
    this.filteredConsultants = this.withAdvisor(this.filteredConsultants);
  }

  private withAdvisor(consultants: BeraterUebersicht[]) {
    return consultants?.map(user => ({
      ...user,
      superVisor: this.advisorMap.get(user.superVisorId)
    }));
  }

  getConsultants() {
    this.consultantService.query().subscribe({
      next: (value) => {
        let dateTime = Date.now();
        let date = new Date(dateTime);
        let todayDate = this.dateWithoutTime(date);
        this.benchedConsultants = value.body
          .filter((consultant) => {
            const consultantDateTime = new Date(consultant?.verfuegbar as Date);
            let consultantDate = this.dateWithoutTime(consultantDateTime);
            return consultantDate <= todayDate;
          })
          .sort((a, b) => a.name.localeCompare(b.name));
        this.filteredConsultants = value.body
          .filter((consultant) => {
            const consultantDateTime = new Date(consultant?.verfuegbar as Date);
            let consultantDate = this.dateWithoutTime(consultantDateTime);
            return consultantDate <= todayDate;
          })
          .sort((a, b) => a.name.localeCompare(b.name));
        this.isConsultantsLoading = false;
        this.applyAdvisors();
      }
    });
  }

  dateWithoutTime(date: Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  manageDrawer(consultant: BeraterUebersicht) {
    if (this.selectedConsultant?.id == consultant?.id) {
      this.selectedConsultant = null;
      this.isDrawerOpen = false;
      return;
    }
    this.selectedConsultant = {...consultant};
    this.isDrawerOpen = true;

    this.form.patchValue({
      note: this.selectedConsultant.note.note || ''
    });

    this.originalNote = this.selectedConsultant.note.note || '';
  }

  protected searchForEntry() {
    this.applyFilter();
  }

  get isNoteChanged(): boolean {
    if (!this.selectedConsultant) return false;
    return this.form.value.note !== this.originalNote;
  }

  saveNote() {
    if (!this.selectedConsultant) return;

    const consultantId = this.selectedConsultant.id;
    const note = this.form.value.note;

    this.selectedConsultant.note = {note};
    const index = this.benchedConsultants.findIndex(c => c.id === consultantId);
    if (index !== -1) {
      this.benchedConsultants[index] = {...this.selectedConsultant};
    }

    this.beraterService.updateNotes(consultantId, note).subscribe({
      next: () => {
        this.messageService.success('bench.message.success');
      },
      error: () => {
        this.messageService.error('bench.message.error');
      }
    });
  }

  getAllSkills() {
    this.skillService.query().subscribe({
      next: (value) => {
        this.allSkills = value.content;
        this.sortSkills();
      },
      error: (err) => {
        this.messageService.error(err.message);
      }
    });
  }

  applyFilter() {
    this.filteredConsultants = this.sorterService.applyFilter(
      this.benchedConsultants,
      this.filter
    );
    this.countFilterAmount();
  }

  countFilterAmount() {
    this.amount = Object.values(this.filter).filter((value) => {
      if (typeof value === 'boolean') return false;
      if (Array.isArray(value)) return value.length > 0;
      return Boolean(value);
    }).length;
  }

  resetFilter() {
    this.filter = createFilterOptions();
    this.getConsultants();
    this.isConsultantsLoading = true;
    this.amount = 0;
  }

  onDrawerClosed() {
    const noteChanged = this.isNoteChanged;
    this.selectedConsultant = null;
    this.isDrawerOpen = false;

    if (noteChanged) {
      this.isConsultantsLoading = true;
      this.getConsultants();
    }
  }

  sortBench(sort: Sort) {
    const data = this.benchedConsultants.slice();
    if (!sort.active || sort.direction === '') {
      this.filteredConsultants = data;
      return;
    }
    this.filteredConsultants = this.withAdvisor(this.filteredConsultants);

    this.filteredConsultants = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'name':
          return this.compare(a.name, b.name, isAsc);
        case 'available':
          return this.compareDate(a.verfuegbar, b.verfuegbar, isAsc);
        case 'role':
          return this.compare(a.position, b.position, isAsc);
        case 'placeOfResidence':
          return this.compare(a.wohnort, b.wohnort, isAsc);
        case 'applicant':
          return this.compareApplicant(a.applicant, b.applicant, isAsc)
        case 'advisor':
          return this.compare(a?.superVisor?.firstName, b?.superVisor?.firstName, isAsc);
        default:
          return 0;
      }
    });
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    if (!a) return 1;
    if (!b) return -1;
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  compareDate(
    a: moment.Moment | Date | string,
    b: moment.Moment | Date | string,
    isAsc: boolean
  ) {
    if (!a) return 1;
    if (!b) return -1;
    const timeA = moment(a).valueOf();
    const timeB = moment(b).valueOf();

    return (timeA - timeB) * (isAsc ? 1 : -1);
  }

  compareApplicant(a: boolean, b: boolean, isAsc: boolean) {
    if (a === b) return 0;

    return isAsc
      ? (a ? -1 : 1)
      : (a ? 1 : -1);
  }
}
